// Tipos para Ordens de Serviço

// Status excluida foi removido - sistema usa hard delete
export type StatusOrdemServico = 
  | 'pendente' 
  | 'aceita' 
  | 'em_execucao' 
  | 'concluida' 
  | 'cancelada' 
  | 'recusada';

export type TipoEventoOS = 
  | 'os_criada' 
  | 'os_aceita' 
  | 'os_recusada' 
  | 'status_alterado'
  | 'prestador_alterado' 
  | 'responsavel_alterado' 
  | 'observacao_adicionada' 
  | 'os_cancelada' 
  | 'os_concluida'
  | 'os_reagendada'
  | 'os_excluida'
  | 'os_reaberta'
  | 'os_editada';

export interface EnderecoOS {
  rua?: string;
  numero?: string;
  complemento?: string;
  cidade?: string;
  codigo_postal?: string;
  regiao?: string;
  pais?: string;
}

export interface ServicoOS {
  tipo: string;
  descricao?: string;
  valor?: number;
}

export interface OrdemServico {
  id: string;
  numero_os: string;
  cliente_id?: string | null;
  prestador_id?: string | null;
  responsavel_id?: string | null;
  servicos: ServicoOS[];
  status: StatusOrdemServico;
  data_solicitacao: string;
  data_agendada?: string | null;
  endereco: EnderecoOS;
  valor_total: number;
  observacoes: string;
  observacoes_internas: string;
  criado_em: string;
  atualizado_em: string;
  // Novos campos de finalização
  tempo_servico_minutos?: number | null;
  data_inicio_execucao?: string | null;
  data_fim_execucao?: string | null;
  fotos_servico?: string[];
  observacoes_finalizacao?: string;
  // Campo de status antes da exclusão (para reabertura)
  status_antes_exclusao?: StatusOrdemServico | null;
  // Campo de comprovativo de pagamento
  comprovativo_pagamento_url?: string | null;
  // Vínculo a uma recorrência (null para OS avulsas)
  recorrencia_id?: string | null;
  // Relacionamentos expandidos
  cliente?: {
    id: string;
    codigo_cliente?: string | null;
    nome: string | null;
    telefone: string;
    email?: string | null;
    avatar_url?: string | null;
  } | null;
  prestador?: {
    id: string;
    profile: { id: string; nome: string; avatar_url?: string | null; telefone?: string | null } | null;
    telefone?: string | null;
  } | null;
  responsavel?: {
    id: string;
    nome: string;
    avatar_url?: string | null;
  } | null;
}

export interface PrestadorServico {
  id: string;
  profile_id: string;
  telefone?: string | null;
  servicos_oferecidos: string[];
  observacoes: string;
  criado_em: string;
  atualizado_em: string;
  // Relacionamento expandido
  profile?: {
    id: string;
    nome: string;
    avatar_url?: string | null;
    telefone?: string | null;
    email?: string | null;
    morada?: string | null;
    codigo_postal?: string | null;
    cidade?: string | null;
    pais?: string | null;
  } | null;
}

export interface EventoOS {
  id: string;
  ordem_servico_id: string;
  usuario_id?: string | null;
  tipo: TipoEventoOS;
  descricao: string;
  detalhes: Record<string, unknown>;
  criado_em: string;
  // Relacionamento expandido
  usuario?: {
    id: string;
    nome: string;
  } | null;
}

// Labels e cores para status (status 'excluida' removido - sistema usa hard delete)
import type { TFunction } from 'i18next';

export const getStatusOSLabels = (t: TFunction): Record<StatusOrdemServico | 'disponiveis', string> => ({
  disponiveis: t('orders.status.available'),
  pendente: t('orders.status.pending'),
  aceita: t('orders.status.accepted'),
  em_execucao: t('orders.status.inProgress'),
  concluida: t('orders.status.completed'),
  cancelada: t('orders.status.cancelled'),
  recusada: t('orders.status.refused'),
});

export const STATUS_OS_COLORS: Record<StatusOrdemServico, string> = {
  pendente: 'bg-yellow-500',
  aceita: 'bg-blue-500',
  em_execucao: 'bg-emerald-500',
  concluida: 'bg-slate-500',
  cancelada: 'bg-red-500',
  recusada: 'bg-red-400',
};

// Labels para eventos
export const getEventoOSLabels = (t: TFunction): Record<TipoEventoOS, string> => ({
  os_criada: t('orders.events.created'),
  os_aceita: t('orders.events.accepted'),
  os_recusada: t('orders.events.refused'),
  status_alterado: t('orders.events.statusChanged'),
  prestador_alterado: t('orders.events.providerChanged'),
  responsavel_alterado: t('orders.events.responsibleChanged'),
  observacao_adicionada: t('orders.events.noteAdded'),
  os_cancelada: t('orders.events.cancelled'),
  os_concluida: t('orders.events.completed'),
  os_reagendada: t('orders.events.rescheduled'),
  os_excluida: t('orders.events.deleted'),
  os_reaberta: t('orders.events.reopened'),
  os_editada: t('orders.events.edited'),
});

// Ícones para eventos (classes Lucide)
export const EVENTO_OS_ICONS: Record<TipoEventoOS, string> = {
  os_criada: 'FilePlus',
  os_aceita: 'CheckCircle',
  os_recusada: 'XCircle',
  status_alterado: 'RefreshCw',
  prestador_alterado: 'UserCog',
  responsavel_alterado: 'UserCheck',
  observacao_adicionada: 'MessageSquare',
  os_cancelada: 'Ban',
  os_concluida: 'CheckCheck',
  os_reagendada: 'CalendarClock',
  os_excluida: 'Trash2',
  os_reaberta: 'RotateCcw',
  os_editada: 'Edit',
};

// Utilitários
export function formatarEndereco(endereco: EnderecoOS, t: TFunction): string {
  const partes: string[] = [];
  
  if (endereco.rua) {
    let linha = endereco.rua;
    if (endereco.numero) linha += `, ${endereco.numero}`;
    if (endereco.complemento) linha += ` - ${endereco.complemento}`;
    partes.push(linha);
  }
  
  if (endereco.cidade || endereco.codigo_postal) {
    let linha = '';
    if (endereco.cidade) linha += endereco.cidade;
    if (endereco.codigo_postal) linha += linha ? ` - ${endereco.codigo_postal}` : endereco.codigo_postal;
    partes.push(linha);
  }
  
  if (endereco.pais) partes.push(endereco.pais);
  
  return partes.join('\n') || t('orders.details.noAddress');
}

export function calcularValorTotal(servicos: ServicoOS[]): number {
  return servicos.reduce((total, s) => total + (s.valor || 0), 0);
}

export function formatarTempo(minutos: number): string {
  if (minutos < 60) {
    return `${minutos} min`;
  }
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  return mins > 0 ? `${horas}h ${mins}min` : `${horas}h`;
}
